import { FetcherResponse } from '../useList.types';
import { InfinityListState } from './infinityList.types';

type InfinityListReducerActions<T> =
  | {
      type: 'FETCHING_MORE';
    }
  | {
      type: 'FETCHING_PAGE';
    }
  | {
      type: 'CLEAR_SELECTEDS';
    }
  | {
      type: 'FETCH_MORE_RESULT';
      payload: FetcherResponse<T>;
    }
  | {
      type: 'FETCH_PAGE_RESULT';
      payload: { response: FetcherResponse<T>; page?: number };
    }
  | {
      type: 'TOGGLE_SELECT_ITEM';
      payload: string;
    }
  | {
      type: 'TOGGLE_SELECT_ALL';
      payload?: string;
    }
  | {
      type: 'TOGGLE_SELECT_ALL_WITH_PARENTS';
      payload?: string;
    }
  | {
      type: 'TOGGLE_SELECT_ITEMS';
      payload: string[];
    }
  | {
      type: 'RESET_STATE';
    }
  | {
      type: 'UPDATE_LIST_ITEM';
      payload: { index: number; item: T };
    }
  | {
      type: 'UPDATE_CUSTOM_COUNT';
      payload: number;
    }
  | {
      type: 'UPDATE_RAW_COUNT';
      payload: number;
    }
  | {
      type: 'REMOVE_LIST_ITEM';
      payload: { index: number };
    }
  | {
      type: 'REPLACE_ALL_ITEMS';
      payload: T[];
    };

export const infinityListInitialState: InfinityListState<any> = {
  isLoadingMore: false,
  isLoadingPage: false,
  page: 0,
  items: [],
  totalItems: 0,
  hasMore: false,
  selecteds: [],
  customCount: undefined,
  rawCount: undefined,
};

export const infinityListReducer =
  <T>() =>
  (state: InfinityListState<T>, action: InfinityListReducerActions<T>): InfinityListState<T> => {
    switch (action.type) {
      case 'FETCHING_MORE':
        return {
          ...state,
          isLoadingPage: false,
          isLoadingMore: true,
        };
      case 'FETCHING_PAGE':
        return {
          ...state,
          isLoadingPage: true,
          isLoadingMore: false,
        };
      case 'CLEAR_SELECTEDS':
        return { ...state, selecteds: [] };
      case 'FETCH_MORE_RESULT': {
        const response = action.payload;

        if (response.tag === 'error') {
          return {
            ...state,
            isLoadingMore: false,
            isLoadingPage: false,
            items: [],
          };
        }

        const newItems = [...state.items, ...response.data];
        return {
          ...state,
          isLoadingPage: false,
          isLoadingMore: false,
          items: newItems,
          totalItems: response.count,
          hasMore: newItems.length < (state.customCount ?? response.count),
          page: state.page + 1,
        };
      }
      case 'FETCH_PAGE_RESULT': {
        const { response, page } = action.payload;

        if (response.tag === 'error') {
          return {
            ...state,
            isLoadingPage: false,
            isLoadingMore: false,
            items: [],
          };
        }

        return {
          ...state,
          isLoadingPage: false,
          isLoadingMore: false,
          items: response.data,
          totalItems: response.count,
          hasMore: response.data.length < (state.customCount ?? response.count),
          page: page || 1,
        };
      }
      case 'TOGGLE_SELECT_ITEM': {
        const isSelected = state.selecteds.includes(action.payload);

        return {
          ...state,
          selecteds: isSelected
            ? state.selecteds.filter((selectedId) => action.payload !== selectedId)
            : [...state.selecteds, action.payload],
        };
      }
      case 'TOGGLE_SELECT_ITEMS': {
        return {
          ...state,
          selecteds: action.payload,
        };
      }
      case 'TOGGLE_SELECT_ALL': {
        const field: string = action.payload || 'id';

        if (state.selecteds.length !== 0) {
          return { ...state, selecteds: [] };
        }

        const selecteds: string[] = state.items.map((item: any) => item[field]);
        return {
          ...state,
          selecteds,
        };
      }
      case 'TOGGLE_SELECT_ALL_WITH_PARENTS': {
        const field: string = action.payload || 'id';

        if (state.selecteds.length !== 0) {
          return { ...state, selecteds: [] };
        }

        const selecteds: string[] = [];
        state.items.forEach((item: any) => {
          if (item.isParent) {
            item.subRows.forEach((subItem: any) => selecteds.push(subItem[field]));
            return;
          }
          selecteds.push(item[field]);
        });
        return {
          ...state,
          selecteds,
        };
      }
      case 'UPDATE_CUSTOM_COUNT': {
        return {
          ...state,
          customCount: action.payload,
        };
      }
      case 'UPDATE_RAW_COUNT': {
        return {
          ...state,
          rawCount: action.payload,
        };
      }
      case 'RESET_STATE':
        return infinityListInitialState;

      case 'UPDATE_LIST_ITEM': {
        const { index, item } = action.payload;
        const newItems = [...state.items];
        newItems[index] = item;
        return { ...state, items: newItems };
      }

      case 'REMOVE_LIST_ITEM': {
        const { index } = action.payload;

        return {
          ...state,
          items: state.items.filter((_, i) => i !== index),
          totalItems: state.totalItems - 1,
          hasMore: state.items.length - 1 < (state.customCount ?? state.totalItems),
        };
      }
      case 'REPLACE_ALL_ITEMS': {
        return {
          ...state,
          items: action.payload,
        };
      }
      default:
        return state;
    }
  };

export const fetchingMore = <T>(): InfinityListReducerActions<T> => ({ type: 'FETCHING_MORE' });
export const fetchingPage = <T>(): InfinityListReducerActions<T> => ({ type: 'FETCHING_PAGE' });
export const clearSelecteds = <T>(): InfinityListReducerActions<T> => ({ type: 'CLEAR_SELECTEDS' });
export const fetchMoreResult = <T>(payload: FetcherResponse<T>): InfinityListReducerActions<T> => ({
  type: 'FETCH_MORE_RESULT',
  payload,
});
export const fetchPageResult = <T>(payload: {
  response: FetcherResponse<T>;
  page?: number;
}): InfinityListReducerActions<T> => ({
  type: 'FETCH_PAGE_RESULT',
  payload,
});
export const selectItem = <T>(payload: string): InfinityListReducerActions<T> => ({
  type: 'TOGGLE_SELECT_ITEM',
  payload,
});
export const selectItems = <T>(payload: string[]): InfinityListReducerActions<T> => ({
  type: 'TOGGLE_SELECT_ITEMS',
  payload,
});
export const selectAll = <T>(field?: string): InfinityListReducerActions<T> => ({
  type: 'TOGGLE_SELECT_ALL',
  payload: field,
});

export const selectAllWithParents = <T>(field?: string): InfinityListReducerActions<T> => ({
  type: 'TOGGLE_SELECT_ALL_WITH_PARENTS',
  payload: field,
});

export const resetState = <T>(): InfinityListReducerActions<T> => ({ type: 'RESET_STATE' });

export const updateListItem = <T>(index: number, item: T): InfinityListReducerActions<T> => ({
  type: 'UPDATE_LIST_ITEM',
  payload: { index, item },
});

export const updateCustomCount = <T>(count: number): InfinityListReducerActions<T> => ({
  type: 'UPDATE_CUSTOM_COUNT',
  payload: count,
});

export const updateRawCount = <T>(count: number): InfinityListReducerActions<T> => ({
  type: 'UPDATE_RAW_COUNT',
  payload: count,
});

export const removeListItem = <T>(index: number): InfinityListReducerActions<T> => ({
  type: 'REMOVE_LIST_ITEM',
  payload: { index },
});

export const replaceAllItems = <T>(items: T[]): InfinityListReducerActions<T> => ({
  type: 'REPLACE_ALL_ITEMS',
  payload: items,
});
